import { makeAuthenticatedRequest } from './authService';
import { logApiConfig } from '../config/api';

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  logApiConfig();
}



export interface DailyNote {
  id: number;
  note_id: string;
  user_id: number;
  note_date: string; // YYYY-MM-DD format
  notes: string; // JSON string with block_ids structure
  attachments?: Array<{
    type: 'image' | 'file' | 'link' | 'voice_memo';
    file_name: string;
    file_path: string;
    file_size?: number;
    mime_type?: string;
    title?: string;
  }>;
  created_at: string;
  updated_at: string;
}

export interface CreateDailyNoteData {
  user_id: number;
  note_date: string; // YYYY-MM-DD format
  notes: string; // JSON string with block_ids structure
  attachments?: Array<{
    type: 'image' | 'file' | 'link' | 'voice_memo';
    file_name: string;
    file_path: string;
    file_size?: number;
    mime_type?: string;
    title?: string;
  }>;
}

export interface UpdateDailyNoteData {
  notes: string; // JSON string with block_ids structure
  attachments?: Array<{
    type: 'image' | 'file' | 'link' | 'voice_memo';
    file_name: string;
    file_path: string;
    file_size?: number;
    mime_type?: string;
    title?: string;
  }>;
}

export interface GetUserNotesParams {
  start_date?: string; // YYYY-MM-DD format
  end_date?: string; // YYYY-MM-DD format
  page?: number;
  limit?: number;
  sort_order?: 'ASC' | 'DESC';
}

export interface SearchNotesParams {
  user_id: number;
  query: string;
  start_date?: string;
  end_date?: string;
  page?: number;
  limit?: number;
}

export interface PaginationResponse {
  current_page: number;
  per_page: number;
  total_items: number;
  total_pages: number;
  has_next_page: boolean;
  has_prev_page: boolean;
}

export interface DailyNotesResponse {
  success: boolean;
  data: DailyNote[];
  pagination?: PaginationResponse;
  search_query?: string;
}

export interface SingleNoteResponse {
  success: boolean;
  data: DailyNote;
  message?: string;
}

export class DailyNotesService {
  /**
   * Create a new daily note
   */
  static async createDailyNote(noteData: CreateDailyNoteData): Promise<DailyNote> {
    const response = await makeAuthenticatedRequest('/daily-notes', {
      method: 'POST',
      body: JSON.stringify(noteData)
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to create daily note');
    }

    return response.data;
  }

  /**
   * Get a daily note by note_id
   */
  static async getDailyNoteById(noteId: string): Promise<DailyNote> {
    const response = await makeAuthenticatedRequest(`/daily-notes/${noteId}`);

    if (!response.success) {
      throw new Error('Failed to fetch daily note');
    }

    return response.data;
  }

  /**
   * Get user's daily notes with optional filtering and pagination
   */
  static async getUserDailyNotes(userId: number, params: GetUserNotesParams = {}): Promise<DailyNotesResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.sort_order) queryParams.append('sort_order', params.sort_order);

    const url = `/daily-notes/user/${userId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await makeAuthenticatedRequest(url);

    if (!response.success) {
      throw new Error('Failed to fetch user daily notes');
    }

    return response;
  }

  /**
   * Get daily note for a specific user and date (now auto-creates empty note if none exists)
   */
  static async getDailyNoteByDate(userId: number, date: string): Promise<DailyNote | null> {
    try {
      const response = await makeAuthenticatedRequest(`/daily-notes/user/${userId}/date/${date}`);
      
      if (!response.success) {
        return null; // Unexpected error
      }

      return response.data;
    } catch (error: any) {
      console.error('Error getting daily note by date:', error);
      throw error;
    }
  }

  /**
   * Ensure a daily note exists for a specific user and date (explicit create-or-get)
   */
  static async ensureDailyNoteByDate(userId: number, date: string): Promise<DailyNote> {
    const response = await makeAuthenticatedRequest(`/daily-notes/user/${userId}/date/${date}/ensure`, {
      method: 'POST'
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to ensure daily note exists');
    }

    return response.data;
  }

  /**
   * Update a daily note
   */
  static async updateDailyNote(noteId: string, updateData: UpdateDailyNoteData): Promise<DailyNote> {
    const response = await makeAuthenticatedRequest(`/daily-notes/${noteId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to update daily note');
    }

    return response.data;
  }

  /**
   * Delete a daily note
   */
  static async deleteDailyNote(noteId: string): Promise<void> {
    const response = await makeAuthenticatedRequest(`/daily-notes/${noteId}`, {
      method: 'DELETE'
    });

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete daily note');
    }
  }

  /**
   * Search daily notes by content
   */
  static async searchDailyNotes(params: SearchNotesParams): Promise<DailyNotesResponse> {
    const queryParams = new URLSearchParams();
    
    queryParams.append('user_id', params.user_id.toString());
    queryParams.append('query', params.query);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    const response = await makeAuthenticatedRequest(`/daily-notes/search?${queryParams.toString()}`);

    if (!response.success) {
      throw new Error('Failed to search daily notes');
    }

    return response;
  }

  /**
   * Create or update today's note for a user
   */
  static async createOrUpdateTodaysNote(userId: number, notes: string, attachments?: any[]): Promise<DailyNote> {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    
    // Try to get existing note for today
    const existingNote = await this.getDailyNoteByDate(userId, today);
    
    if (existingNote) {
      // Update existing note
      return await this.updateDailyNote(existingNote.note_id, { notes, attachments });
    } else {
      // Create new note
      return await this.createDailyNote({
        user_id: userId,
        note_date: today,
        notes,
        attachments
      });
    }
  }

  /**
   * Get today's note for a user
   */
  static async getTodaysNote(userId: number): Promise<DailyNote | null> {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    return await this.getDailyNoteByDate(userId, today);
  }

  /**
   * Get notes for a specific date range
   */
  static async getNotesInRange(userId: number, startDate: string, endDate: string): Promise<DailyNote[]> {
    const response = await this.getUserDailyNotes(userId, {
      start_date: startDate,
      end_date: endDate,
      limit: 100, // Get up to 100 notes in range
      sort_order: 'DESC'
    });

    return response.data;
  }
}

export default DailyNotesService;

